import type { FormDefinition, SubmissionAnswer } from "@responsescope/shared";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export async function fetchForm(slug: string): Promise<FormDefinition> {
  const res = await fetch(`${API_BASE_URL}/api/forms/${slug}`);
  if (!res.ok) throw new Error(`failed to load form: ${res.status}`);
  return (await res.json()) as FormDefinition;
}

export interface GeoResult {
  prefecture: string | null;
}

// Best-effort region auto-detection (Cloudflare edge geo, see
// ARCHITECTURE.md#region-auto-detection) - never blocks form load and never
// throws; a failure just means no pre-fill, the user picks manually.
export async function fetchGeo(): Promise<GeoResult> {
  try {
    const res = await fetch(`${API_BASE_URL}/api/geo`);
    if (!res.ok) return { prefecture: null };
    return (await res.json()) as GeoResult;
  } catch {
    return { prefecture: null };
  }
}

export interface SubmitResult {
  receiptNo: string;
  submissionId: string;
}

export async function submitForm(
  formId: string,
  answers: SubmissionAnswer[],
  files: { fieldKey: string; file: File }[],
  initData: string
): Promise<SubmitResult> {
  const body = new FormData();
  body.set("initData", initData);
  body.set("formId", formId);
  body.set("answers", JSON.stringify(answers));
  for (const { fieldKey, file } of files) {
    body.append(`attachment:${fieldKey}`, file, file.name);
  }

  const res = await fetch(`${API_BASE_URL}/api/submissions`, { method: "POST", body });
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    throw new Error(`submit failed: ${res.status} ${JSON.stringify(errBody)}`);
  }
  return (await res.json()) as SubmitResult;
}
